/* eslint-disable lingui/no-unlocalized-strings */

import { Button, RichInput } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import get from "lodash.get";
import { useMemo } from "react";

import {
  isScoredFieldPath,
  UNSCORED_SUGGESTION_NOTE,
} from "@/client/pages/coach/optimize/coach-scoreable-fields";
import type { SkillAddition } from "@/client/pages/coach/optimize/coach-skill-additions";
import { resolveDiffBeforeText } from "@/client/pages/coach/optimize/original-content";
import {
  COACH_ADDED_TEXT_CLASSES,
  COACH_DIFF_COLORS,
} from "@/client/pages/coach/optimize/coach-theme";
import type { OptimizationSuggestion } from "@/client/pages/dashboard/ai-optimization/review";
import {
  useCoachOptimizationStore,
  useIsCoachOptimizing,
} from "@/client/stores/coach-optimization";
import { useResumeStore } from "@/client/stores/resume";

import {
  COACH_SKILL_ADDITIONS_SECTION_ID,
  filterSuggestionsBySection,
} from "./coach-suggestion-sections";
import { useCoachLiveScore } from "./use-coach-live-score";
import { useCoachSuggestionActions } from "./use-coach-suggestion-actions";

const stripHtml = (value?: string) =>
  (value ?? "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

type GainPillProps = { gain: number };

const GainPill = ({ gain }: GainPillProps) => {
  if (gain <= 0) return null;

  return (
    <span className="shrink-0 rounded bg-success/10 px-1.5 py-0.5 text-[10px] font-medium text-success">
      +{Math.round(gain)}
    </span>
  );
};

/**
 * Badge for a change the match score cannot reward.
 *
 * Shown in place of the "+N" pill on any field the LMS scorer does not receive — every experience,
 * project, certification and education narrative. Those rewrites used to display a "+N" they could
 * never deliver, which is what produced a panel projecting 38 against a real score of 25.
 *
 * Labelled rather than hidden, and worded around what the change IS worth: "no score change" on its
 * own reads as "pointless", and a student who concludes that will skip rewrites that genuinely
 * improve how a recruiter reads their résumé.
 */
const ReadabilityPill = () => (
  <span
    className="shrink-0 rounded bg-foreground/[0.06] px-1.5 py-0.5 text-[10px] font-medium text-foreground/50"
    title={UNSCORED_SUGGESTION_NOTE}
  >
    readability
  </span>
);

/**
 * AI suggestions for ONE resume section, rendered inside that section's editor.
 *
 * Replaces the single stacked review panel that used to sit above every section: a skills rewrite
 * appearing in a list at the top of the sidebar gave the student no way to see it next to the
 * skills they actually have. Each card now lives where its change lands, so accepting is a local,
 * checkable edit rather than a leap of faith.
 *
 * Accepting writes straight into the resume store, so the centre preview and the existing debounced
 * autosave both pick it up with no extra plumbing.
 */
const SectionSuggestions = ({
  suggestions,
  skillAdditions,
}: {
  suggestions: OptimizationSuggestion[];
  skillAdditions: SkillAddition[];
}) => {
  const {
    acceptedIds,
    drafts,
    resumeData,
    originalValues,
    onAccept,
    onReject,
    onEdit,
    onAcceptSkill,
    onRejectSkill,
  } = useCoachSuggestionActions();

  // Same projection the score panel reads, so a card's "+N" and the ring can never disagree.
  const marginalGain = useCoachLiveScore()?.marginalGain;

  return (
    <div className="grid min-w-0 gap-y-3">
      {/* Skills the résumé already demonstrates but does not list. Listing a held skill is the
          single largest movement available on skillOverlap, and it is the least risky change on
          offer — nothing is reworded, only surfaced. Each card carries the résumé text that
          justifies it, so the student can check the claim rather than trust it. */}
      {skillAdditions.length > 0 && (
        <div className="grid gap-y-2 rounded-md border border-border bg-secondary-accent/20 p-3">
          <p className="text-xs font-medium text-foreground/80">Skills to add</p>
          <p className="text-[11px] leading-relaxed text-foreground/55">
            This job asks for these. Add the ones that are true for you.
          </p>

          {skillAdditions.map((addition) => {
            const isAccepted = acceptedIds.includes(addition.id);

            return (
              <div
                key={addition.id}
                className={cn(
                  "grid gap-y-1.5 rounded border border-border p-2.5",
                  isAccepted && "border-success/40",
                )}
              >
                <div className="flex items-center justify-between gap-x-2">
                  <span className="truncate text-xs font-medium">{addition.name}</span>
                  <GainPill gain={marginalGain?.(addition.id) ?? 0} />
                </div>

                {/* Only the `evidenced` case is quoted now. The `unverified` line — "Only add if
                    true: <reason>" — is gone at the author's request: the card's own framing ("add
                    the ones that are true for you") already carries the caveat, and repeating it per
                    row turned a scannable list of skills into a wall of hedging.
                    ⚠️ The confidence distinction itself is NOT gone. classifySkillAdditions still
                    computes it, and it still decides whether a skill is presented as already-held or
                    as the student's call. */}
                {addition.confidence === "evidenced" && (
                  <p className="break-words text-[11px] italic leading-relaxed text-foreground/55">
                    Already in your resume: {addition.evidence}
                  </p>
                )}

                <div className="flex gap-x-1.5">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!isAccepted}
                    className={cn(
                      "h-7 flex-1 text-[11px] disabled:opacity-100",
                      !isAccepted && "border-[#81423A] bg-[#81423A]/15 text-[#81423A]",
                      isAccepted && "border-[#81423A]/50 text-[#81423A] hover:bg-[#81423A]/10",
                    )}
                    onClick={() => onRejectSkill(addition)}
                  >
                    Reject
                  </Button>
                  <Button
                    size="sm"
                    className={cn(
                      "h-7 flex-1 text-[11px] disabled:opacity-100",
                      isAccepted && "bg-[#5F9D74] text-white",
                    )}
                    disabled={isAccepted}
                    onClick={() => onAcceptSkill(addition)}
                  >
                    {isAccepted ? "Added" : "Add"}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {suggestions.map((suggestion) => {
        const isAccepted = acceptedIds.includes(suggestion.id);
        // The struck-out half. NOT a live read of the résumé at this fieldPath: accept
        // writes the suggestion to that path, so reading it made the red "before" line
        // turn into the green "after" text the moment a card was accepted. See
        // resolveDiffBeforeText for the accepted / unaccepted split.
        const currentText = stripHtml(
          resolveDiffBeforeText({
            suggestion,
            isAccepted,
            liveValue: get(resumeData, suggestion.fieldPath) as string | undefined,
            originals: originalValues,
          }),
        );

        return (
          <article
            key={suggestion.id}
            className={cn(
              "min-w-0 overflow-hidden rounded-md border border-border bg-secondary-accent/20 p-3",
              isAccepted && "border-success/40",
            )}
          >
            {/* What this specific change is worth. Requirement: the score moves by the accepted
                changes — so each card states its own contribution rather than leaving the student
                to infer it from a ring. The marginal value is used, not the raw claimed gain: near
                the ceiling the honest number is smaller, and overstating it here is what erodes
                trust in the panel. */}
            <div className="mb-2 flex items-center justify-between gap-x-2">
              <p className="truncate text-xs font-medium text-foreground/80">
                {suggestion.itemLabel}
              </p>
              {isScoredFieldPath(suggestion.fieldPath) ? (
                <GainPill gain={marginalGain?.(suggestion.id) ?? 0} />
              ) : (
                <ReadabilityPill />
              )}
            </div>

            {/* Spelled out under the badge, not left to a tooltip. A student deciding whether to
                accept needs to know this one will not move the number, or they will read the score
                standing still as the feature being broken. */}
            {!isScoredFieldPath(suggestion.fieldPath) && (
              <p className="mb-2 text-[10px] leading-relaxed text-foreground/45">
                Improves how this reads for recruiters. Won&apos;t change your match score.
              </p>
            )}

            {/* ONE field holding both halves of the diff. The struck-out current wording used to sit
                outside the editor's own border, which read as two unrelated boxes — a paragraph, then
                a text input — rather than "this becomes that". The editor's border is dropped
                (`border-0`) so this container is the only frame. */}
            <div className="rounded border border-border bg-background/40 p-2.5 focus-within:border-primary">
              <p
                className="break-words text-xs leading-relaxed line-through"
                style={{ color: COACH_DIFF_COLORS.removed }}
              >
                {currentText}
              </p>

              <RichInput
                content={drafts[suggestion.id] ?? suggestion.suggestedText}
                onChange={(value) => onEdit(suggestion, value)}
                className="mt-2.5 border-0 bg-transparent"
                editorClassName={cn(
                  // eslint-disable-next-line lingui/no-unlocalized-strings
                  "max-h-[180px] min-h-[110px] max-w-full overflow-y-auto overflow-x-hidden break-words px-0 text-xs leading-relaxed",
                  COACH_ADDED_TEXT_CLASSES,
                )}
              />
            </div>

            {/* Exactly one of these is actionable at a time. The active state is not clickable: an
                enabled "Accepted" button invites a click that does nothing, and re-rejecting an
                already-rejected suggestion would re-run the restore for no reason. Untouched counts
                as rejected — nothing has been written to the résumé yet. */}
            <div className="mt-2.5 grid grid-cols-2 gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={!isAccepted}
                className={cn(
                  "h-8 text-xs disabled:opacity-100",
                  !isAccepted && "border-[#81423A] bg-[#81423A]/15 text-[#81423A]",
                  isAccepted && "border-[#81423A]/50 text-[#81423A] hover:bg-[#81423A]/10",
                )}
                onClick={() => onReject(suggestion)}
              >
                Reject
              </Button>
              <Button
                size="sm"
                disabled={isAccepted}
                className={cn(
                  "h-8 text-xs disabled:opacity-100",
                  isAccepted && "bg-[#5F9D74] text-white ring-1 ring-[#5F9D74]",
                )}
                onClick={() => onAccept(suggestion)}
              >
                {isAccepted ? "Accepted" : "Accept"}
              </Button>
            </div>
          </article>
        );
      })}
    </div>
  );
};

/**
 * Gate for the inline suggestions of a single section.
 *
 * Deliberately split from the renderer above so that `useCoachLiveScore` — which flattens the whole
 * résumé to text on every change — is only called by sections that actually have suggestions.
 * Mounted from `SectionBase`, this component renders in all ~15 sections at once; calling the
 * projection hook unconditionally in each would recompute it fifteen times per keystroke.
 */
export const CoachSectionSuggestions = ({ sectionId }: { sectionId: string }) => {
  // Gated on resume id as well as session presence, so an abandoned coach session cannot bleed
  // suggestion cards into an unrelated resume the student opens later.
  const resumeId = useResumeStore((state) => state.resume.id);
  const isCoachOptimizing = useIsCoachOptimizing(resumeId);

  const suggestions = useCoachOptimizationStore((state) => state.suggestions);
  const skillAdditions = useCoachOptimizationStore((state) => state.skillAdditions);

  const sectionSuggestions = useMemo(
    () => filterSuggestionsBySection(suggestions, sectionId),
    [suggestions, sectionId],
  );

  const sectionSkillAdditions =
    sectionId === COACH_SKILL_ADDITIONS_SECTION_ID ? skillAdditions : [];

  if (!isCoachOptimizing) return null;
  if (sectionSuggestions.length === 0 && sectionSkillAdditions.length === 0) return null;

  return (
    <SectionSuggestions suggestions={sectionSuggestions} skillAdditions={sectionSkillAdditions} />
  );
};
